import {
  getCookie,
  setCookie,
  getCart,
  setCartAttributes,
} from "@theme/utilities";
import { WebSourceCodesSet } from "@theme/web-source-code.util";

/**
 * Process tracking codes from URL parameters and sync with cart
 * @returns {Promise<void>}
 */
export async function processTrackingCodes() {
  const namespace = "[TrackingCodes]";
  const currentUrl = new URL(window.location.href);

  const webSourceCodeCookie = "bodi-web-source-code";
  const royaltyCodeCookie = "bodi-royalty-code";
  const cookieMaxAge = 30 * 24 * 60 * 60; // 30 days
  const webSourceCodeCartAttributeName = "webSourceCode";
  const royaltyCodeCartAttributeName = "subscriptionRoyaltyCode";
  const codeParam = currentUrl.searchParams.get("code");
  const rcParam = currentUrl.searchParams.get("rc");
  const existingWebSourceCodeCookie = getCookie(webSourceCodeCookie);
  const existingRoyaltyCodeCookie = getCookie(royaltyCodeCookie);

  /**
   * Set the web source codes to the cart
   * @param {string} webSourceCodes
   */
  async function setWebSourceCodesToCart(webSourceCodes) {
    try {
      console.info(`[${namespace}] - Setting web source codes to cart`, { webSourceCodes });
      await setCartAttributes({ [webSourceCodeCartAttributeName]: webSourceCodes });
      console.info(`[${namespace}] - Web source codes set to cart successfully`, {
        webSourceCodes,
      });
    } catch (error) {
      console.error(`[${namespace}] - Set web source codes to cart failed`, {
        webSourceCodes,
        error,
      });
    }
  }

  /**
   * Set the royalty code to the cart
   * @param {string} royaltyCode
   */
  async function setRoyaltyCodeToCart(royaltyCode) {
    try {
      console.info(`[${namespace}] - Setting royalty code to cart`, { royaltyCode });
      await setCartAttributes({ [royaltyCodeCartAttributeName]: royaltyCode });
      console.info(`[${namespace}] - Royalty code set to cart successfully`, { royaltyCode });
    } catch (error) {
      console.error(`[${namespace}] - Set royalty code to cart failed`, {
        royaltyCode,
        error,
      });
    }
  }

  /**
   * if the code parameter is present in the URL,
   * 1. add it to the web source codes list
   * 2. update the cookie
   * 3. update the cart with the web source codes
   */
  if (codeParam) {
    console.info(`[${namespace}] - Code parameter found in URL`, { codeParam });

    const webSourceCodes = new WebSourceCodesSet(existingWebSourceCodeCookie);
    webSourceCodes.add(codeParam);
    const serializedCodes = webSourceCodes.serialize();

    setCookie(webSourceCodeCookie, serializedCodes, { maxAge: cookieMaxAge });

    await setWebSourceCodesToCart(serializedCodes);
  }

  /**
   * if the rc parameter is present in the URL,
   * 1. update the cookie
   * 2. update the cart with the royalty code
   */
  if (rcParam) {
    console.info(`[${namespace}] - Royalty code parameter found in URL`, { rcParam });

    setCookie(royaltyCodeCookie, rcParam, { maxAge: cookieMaxAge });
    await setRoyaltyCodeToCart(rcParam);
  }

  // If we processed URL parameters, return early
  if (codeParam || rcParam) {
    return;
  }

  /**
   * otherwise, if the web source codes are present in the cookie and are different from the cart,
   * 1. update the cookie
   * 2. update the cart with the web source codes
   */
  const cart = await getCart();

  if (existingWebSourceCodeCookie) {
    console.info(`[${namespace}] - Web source codes found in cookie`, {
      existingWebSourceCodeCookie,
    });
    if (existingWebSourceCodeCookie !== cart.attributes[webSourceCodeCartAttributeName]) {
      await setWebSourceCodesToCart(existingWebSourceCodeCookie);
    } else {
      console.info(`[${namespace}] - Web source codes already set to cart`, {
        existingWebSourceCodeCookie,
      });
    }
  }

  /**
   * if the royalty code is present in the cookie and is different from the cart,
   * 1. update the cart with the royalty code
   */
  if (existingRoyaltyCodeCookie) {
    console.info(`[${namespace}] - Royalty code found in cookie`, { existingRoyaltyCodeCookie });
    if (existingRoyaltyCodeCookie !== cart.attributes[royaltyCodeCartAttributeName]) {
      await setRoyaltyCodeToCart(existingRoyaltyCodeCookie);
    } else {
      console.info(`[${namespace}] - Royalty code already set to cart`, {
        existingRoyaltyCodeCookie,
      });
    }
  }

}

(async function TrackingCodesModule() {
  // skip /pages/checkout-start page, it will call processTrackingCodes explicitly
  if (window.location.pathname.includes("/pages/checkout-start")) {
    return;
  }

  await processTrackingCodes();
})();
